import React, { useEffect, useMemo, useRef, useState } from "react";
import { App, Modal, Spin } from "antd";
import { useSelectedResult } from "../../../contexts/SelectedResultContext";
import { CardsProvider } from "../../../contexts/CardsContext";
import PackagePayment from "../../../Pages/Client/PackagesProfile/PackagePayment/PackagePayment";
import { PACKAGE_PROFILE_PAYMENT_CREDIT } from "../../../utils/packageProfileCustomerPayment";
import {
  ensureVacationPackageLdsOrderForPayment,
  fetchExtendedDealRowsForLdsDealId,
  resolveLdsDealIdFromSelectedResult,
} from "../../../utils/vacationPackageEnsureLdsOrderForPayment";
import { getLdsOrder } from "../../../services/fhpService";
import { extractLdsOrderFinalPriceIls } from "../../../utils/extractLdsOrderFinalPriceIls";
import {
  getChatPaymentLockSnapshot,
  reconcileChatPaymentLockToOrderCanonical,
  subscribe as subscribeChatPaymentLock,
} from "../../../stores/customerChatPaymentStore";
import { compactPricingAuditMeta } from "../../../pricing/compactPricingAuditMeta";
import { normalizePricingAuditProductType } from "../../../pricing/pricingAuditProductType";

const CHAT_PAYMENT_TARGET = "lds_tranzila_iframe";

/**
 * מודאל תשלום צ'אט — תצוגת סכום רק מ־LockedPaymentOffer (בלי calculateLdsTotalPayment).
 */
export default function CustomerVacationChatPaymentModal({
  open,
  onClose,
  agentIdFromSession = null,
  bookingChannel = null,
}) {
  const { message } = App.useApp();

  const {
    selectedResult,
    roomsAndPaxInfo,
    ourPrice,
    ldsSupplement,
    extendedDealApiData,
    setExtendedDealApiData,
    ourSavedLDSOrder,
    setOurSavedLDSOrder,
  } = useSelectedResult();

  const [phase, setPhase] = useState("idle");
  const [errorText, setErrorText] = useState("");
  const [prepOrderId, setPrepOrderId] = useState(null);
  const [isConditionsConfirmed, setIsConditionsConfirmed] = useState(false);
  const [customerPaymentMethod, setCustomerPaymentMethod] = useState(
    PACKAGE_PROFILE_PAYMENT_CREDIT
  );
  /** מחיר תצוגה מה-lock בזמן פתיחת המודאל (לא מחושב מחדש). */
  const [lockedDisplayIls, setLockedDisplayIls] = useState(null);

  const paymentPrepSeqRef = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    return subscribeChatPaymentLock(() => {
      const snap = getChatPaymentLockSnapshot();
      const v = snap?.paymentPriceIls;
      if (v != null && Number.isFinite(Number(v))) {
        setLockedDisplayIls(Math.round(Number(v)));
      }
    });
  }, [open]);

  useEffect(() => {
    if (!open) {
      paymentPrepSeqRef.current += 1;
      setPhase("idle");
      setErrorText("");
      setPrepOrderId(null);
      setIsConditionsConfirmed(false);
      setLockedDisplayIls(null);
      return;
    }

    const lockSnap = getChatPaymentLockSnapshot();
    const displayPriceIls =
      lockSnap && lockSnap.paymentPriceIls === lockSnap.approvedPriceIls
        ? Math.round(Number(lockSnap.paymentPriceIls))
        : null;

    if (displayPriceIls == null || !Number.isFinite(displayPriceIls)) {
      setLockedDisplayIls(null);
      setPhase("error");
      setErrorText("חסר מחיר מאושר לתשלום (PRICE_MISSING).");
      return;
    }

    setLockedDisplayIls(displayPriceIls);

    // eslint-disable-next-line no-console
    console.info("CHAT_PAYMENT_MODAL_OPEN_REQUEST", {
      CHAT_PAYMENT_TARGET,
      displayPriceIls,
      offerId: lockSnap?.offerId,
    });

    const seq = ++paymentPrepSeqRef.current;
    let cancelled = false;

    (async () => {
      setPhase("preparing");
      setErrorText("");
      try {
        const ldsDealId = resolveLdsDealIdFromSelectedResult(selectedResult);
        let extended = extendedDealApiData;
        if (ldsDealId && (!Array.isArray(extended) || extended.length === 0)) {
          const rows = await fetchExtendedDealRowsForLdsDealId(ldsDealId);
          if (cancelled || seq !== paymentPrepSeqRef.current) return;
          if (rows?.length) {
            extended = rows;
            setExtendedDealApiData(rows);
          }
        }

        const agentForOrder = lockSnap?.agentId ?? agentIdFromSession;
        const orderData = await ensureVacationPackageLdsOrderForPayment({
          selectedResult,
          roomsAndPaxInfo,
          ourPrice,
          ldsSupplement,
          extendedDealApiData: extended,
          ourSavedLDSOrder,
          setOurSavedLDSOrder,
          agentId: agentForOrder,
          bookingChannel,
          later: false,
          payableTotalIls: displayPriceIls,
          publicFinalPriceIls: displayPriceIls,
        });
        if (cancelled || seq !== paymentPrepSeqRef.current) return;
        const oid = orderData?.id;
        if (oid == null || String(oid).trim() === "") {
          throw new Error("לא התקבל מזהה הזמנה מהשרת");
        }

        const orderRes = await getLdsOrder({ id: oid });
        if (cancelled || seq !== paymentPrepSeqRef.current) return;
        const row = orderRes?.data;
        const orderFinal = extractLdsOrderFinalPriceIls(row);
        if (orderFinal == null || !Number.isFinite(orderFinal)) {
          throw new Error("לא ניתן לקרוא מחיר סופי מההזמנה — נסו שוב או פנו לנציג.");
        }
        const orderRounded = Math.round(orderFinal);
        const expected = displayPriceIls;
        if (orderRounded !== Math.round(expected)) {
          reconcileChatPaymentLockToOrderCanonical(orderRounded);
          setLockedDisplayIls(orderRounded);
          // eslint-disable-next-line no-console
          console.info("CHAT_PAYMENT_PRICE_RECONCILED_TO_ORDER", {
            previousLockIls: expected,
            orderCanonicalIls: orderRounded,
            orderId: oid,
          });
        }

        // eslint-disable-next-line no-console
        console.info("CHAT_PAYMENT_PRICE_RENDERED", {
          source: "order_canonical_ils",
          paymentPriceIls: orderRounded,
          orderId: oid,
        });

        setPrepOrderId(oid);
        setPhase("ready");
        // eslint-disable-next-line no-console
        console.info("PAYMENT_AUTO_OPEN_SUCCESS", {
          mode: "modal_ready",
          CHAT_PAYMENT_TARGET,
          seq,
        });
        // eslint-disable-next-line no-console
        console.info("[cvac] CHAT_PAYMENT_MODAL_OPENED", {
          CHAT_PAYMENT_SOURCE: "customer_chat",
          CHAT_PAYMENT_TARGET,
          seq,
        });
      } catch (e) {
        if (cancelled || seq !== paymentPrepSeqRef.current) return;
        const raw = e?.message || "לא ניתן להכין תשלום כרגע";
        const msg = raw;
        setErrorText(msg);
        setPhase("error");
        message.error(msg);
        // eslint-disable-next-line no-console
        console.warn("[cvac] CHAT_PAYMENT_PREP_FAILED", {
          CHAT_PAYMENT_SOURCE: "customer_chat",
          CHAT_PAYMENT_TARGET,
          err: String(raw),
          seq,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const orderId = prepOrderId ?? ourSavedLDSOrder?.id ?? null;

  const chatPaymentCardsAuditMeta = useMemo(() => {
    const fullDeal = selectedResult?.fullDeal || selectedResult;
    const hotel = fullDeal?.hotel || selectedResult?.hotel || {};
    const supplierDetails = selectedResult?.supplierDetails;
    const sid =
      supplierDetails?.supplierId ??
      selectedResult?.flight?.[0]?.leg?.supplierId ??
      selectedResult?.supplierId;
    const hc = hotel?.hotelCity;
    return compactPricingAuditMeta({
      refKind: orderId ? "booking" : undefined,
      bookingId: orderId != null ? String(orderId) : undefined,
      channelType: "ai_chat",
      agentId:
        agentIdFromSession != null ? String(agentIdFromSession) : undefined,
      productType: normalizePricingAuditProductType(
        selectedResult?.dealType ?? selectedResult?.dealtype
      ),
      supplierCode: sid != null ? String(sid) : undefined,
      destinationCode:
        typeof hc === "string" && hc.trim() ? hc.trim() : undefined,
      subCategoryCode:
        typeof bookingChannel === "string" && bookingChannel.trim()
          ? bookingChannel.trim()
          : undefined,
      pricingModel: "customerVacationChat:CardsProvider",
    });
  }, [orderId, selectedResult, agentIdFromSession, bookingChannel]);

  return (
    <Modal
      title="תשלום מאובטח"
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(720px, 96vw)"
      destroyOnHidden
      className="hr-cvac-payment-modal"
      zIndex={10060}
    >
      {phase === "preparing" ? (
        <div className="hr-cvac-payment-modal__loading" dir="rtl">
          <Spin size="large" />
          <p>מכינים את ההזמנה ואת חלון התשלום…</p>
        </div>
      ) : null}
      {phase === "error" ? (
        <div className="hr-cvac-payment-modal__err" dir="rtl">
          <p>{errorText}</p>
        </div>
      ) : null}
      {phase === "ready" && orderId && lockedDisplayIls != null ? (
        <div className="hr-cvac-payment-modal__body" dir="rtl">
          <CardsProvider
            totalPrice={lockedDisplayIls}
            pricingAuditMeta={chatPaymentCardsAuditMeta}
          >
            <PackagePayment
              our_order_id={orderId}
              leftToPay={lockedDisplayIls}
              sumpaid={0}
              disabled={false}
              isCustomerLink={true}
              selectedResult={selectedResult}
              isConditionsConfirmed={isConditionsConfirmed}
              setIsConditionsConfirmed={setIsConditionsConfirmed}
              selectedPaymentMethod={customerPaymentMethod}
              onPaymentMethodChange={setCustomerPaymentMethod}
              lockPaymentMethodToBank={false}
            />
          </CardsProvider>
        </div>
      ) : null}
    </Modal>
  );
}
